import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import seoConfig from '../config/seoConfig.json';

export const useSEO = (customSeo = {}, { skip = false } = {}) => {
  const location = useLocation();
  // Serialize so the effect also re-runs when a page passes async data
  // (e.g. a walk-in drive that loads after mount) — not just on path change.
  const customKey = JSON.stringify(customSeo);

  useEffect(() => {
    // Routes that manage their own SEO (e.g. walk-in detail, search) pass
    // skip=true from the App-level call so it doesn't clobber page meta.
    if (skip) return;
    const currentPath = location.pathname;
    const pageSeo = seoConfig.pages[currentPath] || {};
    const defaultSeo = seoConfig.defaultSeo;

    // Merge SEO with priority: customSeo > pageSeo > defaultSeo
    const seoData = {
      ...defaultSeo,
      ...pageSeo,
      ...customSeo
    };

    // PAGE TITLE
    document.title = seoData.title;

    // DEFAULT META TAGS
    updateMeta("description", seoData.description);
    updateMeta("keywords", seoData.keywords);
    updateMeta("author", seoData.author);
    updateMeta("robots", seoData.robots || "index, follow");

    // MOBILE SEO
    updateMeta("viewport", "width=device-width, initial-scale=1");
    updateMeta("theme-color", "#0A66C2"); // JobxBharat primary brand color

    // OPEN GRAPH TAGS
    updateMetaProperty("og:title", seoData.ogTitle || seoData.title);
    updateMetaProperty("og:description", seoData.ogDescription || seoData.description);
    updateMetaProperty("og:type", seoData.ogType || "website");
    updateMetaProperty("og:image", seoData.ogImage);
    updateMetaProperty("og:url", `${seoData.canonical}${currentPath}`);

    // TWITTER TAGS
    updateMeta("twitter:card", seoData.twitterCard);
    updateMeta("twitter:site", seoData.twitterSite);
    updateMeta("twitter:title", seoData.ogTitle || seoData.title);
    updateMeta("twitter:description", seoData.ogDescription || seoData.description);
    updateMeta("twitter:image", seoData.ogImage);

    // CANONICAL URL
    updateCanonical(`${seoData.canonical}${currentPath}`);

    // STRUCTURED DATA (supports arrays)
    removeExistingStructuredData();
    renderStructuredData(seoData);

  }, [location.pathname, customKey, skip]);
};

/* -------------------- HELPERS -------------------- */

const updateMeta = (name, content) => {
  if (!content) return;
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const updateMetaProperty = (property, content) => {
  if (!content) return;
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const updateCanonical = (url) => {
  let link = document.querySelector(`link[rel="canonical"]`);
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = url;
};

// Remove old JSON-LD blocks to prevent duplicates
const removeExistingStructuredData = () => {
  const scripts = document.querySelectorAll('script[type="application/ld+json"]');
  scripts.forEach(s => s.remove());
};

// Insert all structured-data blocks
const renderStructuredData = (seoData) => {
  const allSchemas = [];

  // 1. Organization schema (default)
  if (seoData.organizationStructuredData) {
    allSchemas.push(seoData.organizationStructuredData);
  }

  // 2. Page-specific structuredData (can be object or array)
  if (seoData.structuredData) {
    if (Array.isArray(seoData.structuredData)) {
      allSchemas.push(...seoData.structuredData);
    } else {
      allSchemas.push(seoData.structuredData);
    }
  }

  // 3. FAQ Schema
  if (seoData.faqStructuredData) {
    allSchemas.push(seoData.faqStructuredData);
  }

  if (allSchemas.length === 0) return;

  // Insert all structured data blocks. Escape "<" so a "</script>" that ever
  // appears inside a description can't break out of the JSON-LD script tag in
  // the prerendered HTML.
  allSchemas.forEach(schema => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema).replace(/</g, "\\u003c");
    document.head.appendChild(script);
  });
};
